import { Box } from "@mui/material";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";
import Checkbox from "../Checkbox/Checkbox";
import Radio from "../Radio/Radio";

/**
 * ListItem
 *
 * A row with the label on the left and a Radio (single) or Checkbox (multi) on the right.
 *
 * Props:
 * - children: label (the header)
 * - subheader: a smaller grey line under the header (4px below it)
 * - badge: a node (e.g. a Badge) shown after the header (8px after it)
 * - multi: Checkbox instead of Radio (default: single select, Radio)
 * - checked, defaultChecked, onChange, name, value, disabled: go to the native input.
 *   Radios with the same name form one single-select group.
 * - sx: extra styles for the row, merged last
 */
const ListItem = ({
  multi = false,
  subheader,
  badge,
  children,
  sx: sxOverride = {},
  ...rest
}) => {
  const Control = multi ? Checkbox : Radio;
  // minWidth: 0 all the way down lets long text shrink and show an ellipsis instead of
  // widening the row; the badge and the radio keep their size.
  const truncate = {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  const label =
    subheader || badge ? (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            minWidth: 0,
          }}
        >
          <Box sx={truncate}>{children}</Box>
          {badge && <Box sx={{ flexShrink: 0, display: "flex" }}>{badge}</Box>}
        </Box>
        {subheader && (
          <Box
            sx={{
              ...truncate,
              fontSize: 12,
              lineHeight: "18px",
              letterSpacing: "0.2px",
              color: "#8A8A8A", // Figma hex, not one of the palette tokens
            }}
          >
            {subheader}
          </Box>
        )}
      </Box>
    ) : (
      <Box sx={{ flex: 1, ...truncate }}>{children}</Box>
    );

  return (
    <Control
      {...rest}
      sx={{
        display: "flex",
        flexDirection: "row-reverse",
        justifyContent: "space-between",
        width: "100%",
        padding: "8px 12px",
        boxSizing: "border-box",
        borderRadius: "4px",
        backgroundColor: colors.white,
        // Figma hover: the unselected control takes the filled look
        "&:hover input:not(:checked):not(:disabled) + span": {
          backgroundColor: colorPalette.mercuryAlpha[5],
          borderColor: colorPalette.mercuryAlpha[8],
        },
        ...sxOverride,
      }}
    >
      {label}
    </Control>
  );
};

export default ListItem;
